import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Shield, 
  Upload, 
  Download, 
  Lock, 
  Unlock, 
  Settings, 
  Moon, 
  Sun, 
  Maximize, 
  Minimize, 
  BarChart3,
  Key,
  FileText,
  AlertCircle,
  CheckCircle,
  Eye,
  EyeOff,
  Trash2,
  Copy
} from 'lucide-react';

interface AnalyticsData {
  filesProcessed: number;
  totalSize: number;
  successRate: number;
  averageTime: number;
}

interface SettingsData {
  algorithm: string;
  keyDerivation: string;
  iterations: number;
  autoDownload: boolean;
  deleteAfterProcess: boolean;
}

interface ResultData {
  url: string;
  filename: string;
  size: number;
}

const FileEncryptDecrypt = () => {
  const [isDark, setIsDark] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [operation, setOperation] = useState<'encrypt' | 'decrypt'>('encrypt');
  const [processing, setProcessing] = useState<boolean>(false);
  const [result, setResult] = useState<ResultData | null>(null);
  const [analytics, setAnalytics] = useState<AnalyticsData>({
    filesProcessed: 0,
    totalSize: 0,
    successRate: 100,
    averageTime: 0
  });
  const [settings, setSettings] = useState<SettingsData>({
    algorithm: 'AES-256',
    keyDerivation: 'PBKDF2',
    iterations: 100000,
    autoDownload: true,
    deleteAfterProcess: false
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Analytics tracking
  const trackOperation = useCallback((type: string, success: boolean, fileSize: number, duration: number) => {
    setAnalytics(prev => ({
      filesProcessed: prev.filesProcessed + 1,
      totalSize: prev.totalSize + fileSize,
      successRate: ((prev.successRate * prev.filesProcessed + (success ? 100 : 0)) / (prev.filesProcessed + 1)),
      averageTime: ((prev.averageTime * prev.filesProcessed + duration) / (prev.filesProcessed + 1))
    }));
  }, []);

  // Unicode-safe base64 encoding/decoding
  const encodeUnicode = (str: string): string => {
    return btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (match, p1) => {
      return String.fromCharCode(parseInt(p1, 16));
    }));
  };

  const decodeUnicode = (str: string): string => {
    return decodeURIComponent(atob(str).split('').map(c => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
  };

  // Simple encryption/decryption simulation
  const processFile = useCallback(async () => {
    if (!selectedFile || !password) return;

    setProcessing(true);
    const startTime = Date.now();

    try {
      // Simulate processing time
      await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 2000));

      const reader = new FileReader();
      reader.onload = (e) => {
        if (!e.target || !e.target.result) return;
        
        const content = e.target.result as string;
        let processedContent: string;
        
        if (operation === 'encrypt') {
          // Simple XOR encryption for demo with Unicode support
          const encrypted = Array.from(content)
            .map((char, i) => String.fromCharCode(char.charCodeAt(0) ^ password.charCodeAt(i % password.length)))
            .join('');
          
          // Use Unicode-safe base64 encoding
          processedContent = encodeUnicode(encrypted);
        } else {
          // Simple XOR decryption for demo with Unicode support
          try {
            // Use Unicode-safe base64 decoding
            const decoded = decodeUnicode(content);
            processedContent = Array.from(decoded)
              .map((char, i) => String.fromCharCode(char.charCodeAt(0) ^ password.charCodeAt(i % password.length)))
              .join('');
          } catch {
            throw new Error('Invalid encrypted file or wrong password');
          }
        }

        const blob = new Blob([processedContent], { type: selectedFile.type });
        const url = URL.createObjectURL(blob);
        const filename = `${operation}ed_${selectedFile.name}`;

        setResult({ url, filename, size: blob.size });
        
        const duration = Date.now() - startTime;
        trackOperation(operation, true, selectedFile.size, duration);

        if (settings.autoDownload) {
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.click();
        }

        if (settings.deleteAfterProcess) {
          setSelectedFile(null);
          setPassword('');
        }
      };

      reader.readAsText(selectedFile);
    } catch (error) {
      const duration = Date.now() - startTime;
      trackOperation(operation, false, selectedFile.size, duration);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`Error: ${errorMessage}`);
    } finally {
      setProcessing(false);
    }
  }, [selectedFile, password, operation, settings, trackOperation]);

  // Fullscreen handling
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const copyToClipboard = (text: string): void => {
    navigator.clipboard.writeText(text);
  };

  return (
    <>
      <Helmet>
        <title>File Encrypt & Decrypt - Secure File Protection Tool</title>
        <meta name="description" content="Professional file encryption and decryption tool with AES-256 security. Protect your sensitive files with password-based encryption." />
        <meta name="keywords" content="file encryption, file decryption, AES-256, password protection, secure files, data security" />
        <meta name="author" content="File Security Tools" />
        <meta property="og:title" content="File Encrypt & Decrypt Tool" />
        <meta property="og:description" content="Secure your files with professional-grade encryption and decryption capabilities." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://example.com/file-encrypt-decrypt" />
      </Helmet>

      <div 
        ref={containerRef}
        className={`min-h-screen transition-all duration-300 ${
          isDark ? 'bg-gray-900 text-white' : 'bg-gradient-to-br from-blue-50 to-indigo-100 text-gray-900'
        } ${isFullscreen ? 'p-0' : 'p-4'}`}
      >
        {/* Header */}
        <header className="flex items-center justify-between mb-8 p-4 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20">
          <div className="flex items-center space-x-3">
            <Shield className="w-8 h-8 text-blue-500" />
            <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              File Encrypt & Decrypt
            </h1>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className={`p-2 rounded-lg transition-colors ${
                isDark ? 'hover:bg-gray-700' : 'hover:bg-white/20'
              } ${showAnalytics ? 'bg-blue-500 text-white' : ''}`}
            >
              <BarChart3 className="w-5 h-5" />
            </button>
            
            <button
              onClick={() => setShowSettings(!showSettings)}
              className={`p-2 rounded-lg transition-colors ${
                isDark ? 'hover:bg-gray-700' : 'hover:bg-white/20'
              } ${showSettings ? 'bg-blue-500 text-white' : ''}`}
            >
              <Settings className="w-5 h-5" />
            </button>
            
            <button
              onClick={() => setIsDark(!isDark)}
              className={`p-2 rounded-lg transition-colors ${
                isDark ? 'hover:bg-gray-700' : 'hover:bg-white/20'
              }`}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            
            <button
              onClick={toggleFullscreen}
              className={`p-2 rounded-lg transition-colors ${
                isDark ? 'hover:bg-gray-700' : 'hover:bg-white/20'
              }`}
            >
              {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
            </button>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Panel */}
          <div className="lg:col-span-2">
            <div className={`p-6 rounded-xl ${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white/70 border-white/30'
            } backdrop-blur-sm border`}>
              
              {/* Operation Toggle */}
              <div className="flex mb-6 p-1 bg-gray-100 dark:bg-gray-700 rounded-lg">
                <button
                  onClick={() => setOperation('encrypt')}
                  className={`flex-1 flex items-center justify-center py-2 px-4 rounded-md transition-colors ${
                    operation === 'encrypt'
                      ? 'bg-blue-500 text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Encrypt
                </button>
                <button
                  onClick={() => setOperation('decrypt')}
                  className={`flex-1 flex items-center justify-center py-2 px-4 rounded-md transition-colors ${
                    operation === 'decrypt'
                      ? 'bg-blue-500 text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  <Unlock className="w-4 h-4 mr-2" />
                  Decrypt
                </button>
              </div>

              {/* File Upload */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Select File</label>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                    selectedFile
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                      : `border-gray-300 dark:border-gray-600 hover:border-blue-500 ${
                          isDark ? 'hover:bg-gray-700' : 'hover:bg-blue-50'
                        }`
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={(e) => {
                      const files = e.target.files;
                      if (files && files[0]) {
                        setSelectedFile(files[0]);
                      }
                    }}
                    className="hidden"
                  />
                  
                  {selectedFile ? (
                    <div className="flex items-center justify-center space-x-3">
                      <FileText className="w-8 h-8 text-green-500" />
                      <div className="text-left">
                        <p className="font-medium">{selectedFile.name}</p>
                        <p className="text-sm text-gray-500">{formatFileSize(selectedFile.size)}</p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFile(null);
                        }}
                        className="p-1 hover:bg-red-100 dark:hover:bg-red-900/20 rounded"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  ) : (
                    <div>
                      <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                      <p className="text-lg font-medium mb-2">Drop your file here</p>
                      <p className="text-sm text-gray-500">or click to browse</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Password Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">
                  <Key className="w-4 h-4 inline mr-1" />
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter a strong password"
                    className={`w-full px-4 py-3 pr-12 rounded-lg border ${
                      isDark
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Process Button */}
              <button
                onClick={processFile}
                disabled={!selectedFile || !password || processing}
                className={`w-full py-4 px-6 rounded-lg font-medium transition-all transform ${
                  !selectedFile || !password || processing
                    ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:from-blue-600 hover:to-purple-700 hover:scale-105 shadow-lg'
                }`}
              >
                {processing ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                    Processing...
                  </div>
                ) : (
                  `${operation === 'encrypt' ? 'Encrypt' : 'Decrypt'} File`
                )}
              </button>

              {/* Result */}
              {result && (
                <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="flex items-center mb-2">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                    <span className="font-medium text-green-700 dark:text-green-300">
                      {operation === 'encrypt' ? 'File Encrypted Successfully!' : 'File Decrypted Successfully!'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-300">{result.filename}</p>
                      <p className="text-xs text-gray-500">{formatFileSize(result.size)}</p>
                    </div>
                    <button
                      onClick={() => {
                        const a = document.createElement('a');
                        a.href = result.url;
                        a.download = result.filename;
                        a.click();
                      }}
                      className="flex items-center px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Side Panel */}
          <div className="space-y-6">
            {/* Analytics Panel */}
            {showAnalytics && (
              <div className={`p-4 rounded-xl ${
                isDark ? 'bg-gray-800 border-gray-700' : 'bg-white/70 border-white/30'
              } backdrop-blur-sm border`}>
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <BarChart3 className="w-5 h-5 mr-2" />
                  Analytics
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Files Processed</span>
                    <span className="font-medium">{analytics.filesProcessed}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Total Size</span>
                    <span className="font-medium">{formatFileSize(analytics.totalSize)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Success Rate</span>
                    <span className="font-medium text-green-500">{analytics.successRate.toFixed(1)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Avg. Time</span>
                    <span className="font-medium">{(analytics.averageTime / 1000).toFixed(1)}s</span>
                  </div>
                </div>
              </div>
            )}

            {/* Settings Panel */}
            {showSettings && (
              <div className={`p-4 rounded-xl ${
                isDark ? 'bg-gray-800 border-gray-700' : 'bg-white/70 border-white/30'
              } backdrop-blur-sm border`}>
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <Settings className="w-5 h-5 mr-2" />
                  Advanced Settings
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Algorithm</label>
                    <select
                      value={settings.algorithm}
                      onChange={(e) => setSettings(prev => ({ ...prev, algorithm: e.target.value }))}
                      className={`w-full px-3 py-2 rounded border ${
                        isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                      }`}
                    >
                      <option value="AES-256">AES-256</option>
                      <option value="AES-192">AES-192</option>
                      <option value="AES-128">AES-128</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Key Derivation</label>
                    <select
                      value={settings.keyDerivation}
                      onChange={(e) => setSettings(prev => ({ ...prev, keyDerivation: e.target.value }))}
                      className={`w-full px-3 py-2 rounded border ${
                        isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                      }`}
                    >
                      <option value="PBKDF2">PBKDF2</option>
                      <option value="Argon2">Argon2</option>
                      <option value="scrypt">scrypt</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Iterations</label>
                    <input
                      type="number"
                      value={settings.iterations}
                      onChange={(e) => setSettings(prev => ({ ...prev, iterations: parseInt(e.target.value) || 100000 }))}
                      className={`w-full px-3 py-2 rounded border ${
                        isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                      }`}
                      min="10000"
                      max="1000000"
                      step="10000"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.autoDownload}
                        onChange={(e) => setSettings(prev => ({ ...prev, autoDownload: e.target.checked }))}
                        className="mr-2"
                      />
                      <span className="text-sm">Auto download result</span>
                    </label>
                    
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.deleteAfterProcess}
                        onChange={(e) => setSettings(prev => ({ ...prev, deleteAfterProcess: e.target.checked }))}
                        className="mr-2"
                      />
                      <span className="text-sm">Clear inputs after processing</span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Security Info */}
            <div className={`p-4 rounded-xl ${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white/70 border-white/30'
            } backdrop-blur-sm border`}>
              <h3 className="text-lg font-semibold mb-3 flex items-center">
                <AlertCircle className="w-5 h-5 mr-2 text-blue-500" />
                Security Notes
              </h3>
              <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <p>• Files are processed locally in your browser</p>
                <p>• No data is sent to external servers</p>
                <p>• Use strong, unique passwords</p>
                <p>• Keep your passwords secure</p>
                <p>• This is a demo - use proper encryption tools for sensitive data</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FileEncryptDecrypt;